import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, Link } from 'react-router-dom'
import { Box, Collapse, Group, Pagination, Stack, Table } from '@mantine/core'
import { AppBadge, AppButton, AppCard, AppCheckbox, AppInput, AppModal, AppSelect, AppTabs, AppText, AppTextarea } from '../../app/common/ui'
import { FiCheck, FiMinus, FiSettings } from 'react-icons/fi'
import { Page, FilterBar, AdminTable, ConfirmModal, EmptyState, SidebarLayout } from '../../app/common/ui'
import type { ColumnDef } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { orphanedItemId } from '../../app/stores/adminStore'
import agent from '../../app/api/agent'
import type {
  OrphanedContentDto,
  InternalTagSummaryDto,
  InternalTagContentDto
} from '../../app/models/admin'
import type { MotdDto, MotdHistoryItemDto } from '../../app/models/motd'
import type { PagedResult } from '../../app/models/pagedResult'
import { FeatureFlagsAdminPanel } from './FeatureFlagsAdminPanel'
import { PlatformsAdminPanel } from './PlatformsAdminPanel'
import { format, isValid } from 'date-fns'
import { toast } from 'react-toastify'

const CONTENT_TYPE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Post', value: 'Post' },
  { label: 'Part', value: 'Part' },
  { label: 'FirearmModel', value: 'FirearmModel' },
  { label: 'Build', value: 'Build' }
]

const PAGE_SIZE_ORPHANED = 25
const PAGE_SIZE_LOG = 20
const PAGE_SIZE_MOTD_HISTORY = 20

type AdminContentTab = '0' | '1' | '2' | '3' | '4' | '5' | '6';

function parseAdminContentTab (v: string | null): AdminContentTab {
  if (v === '0' || v === '1' || v === '2' || v === '3' || v === '4' || v === '5' || v === '6') return v
  return '0'
}

function formatMotdSetAtLabel (setAt: string | undefined): string {
  if (!setAt) return 'Unknown date'
  const d = new Date(setAt)
  return isValid(d) ? format(d, 'M-d-yyyy') : 'Unknown date'
}

function formatMotdAuditInstant (iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return isValid(d) ? format(d, 'yyyy-MM-dd HH:mm') + ' UTC' : iso
}

/** Same `AdminTable` grid as Admin → Users (Mantine table + pagination, no paid grid). */
const MOTD_HISTORY_COLUMNS: ColumnDef<MotdHistoryItemDto>[] = [
  {
    key: 'id',
    label: 'Id',
    render: (row) => (
      <AppText size="xs" c="dimmed" ff="monospace" title={row.id}>
        {`${row.id.slice(0, 8)}…`}
      </AppText>
    )
  },
  {
    key: 'setAt',
    label: 'Set (UTC)',
    render: (row) => formatMotdAuditInstant(row.setAt)
  },
  {
    key: 'setByUsername',
    label: 'Set by',
    render: (row) => <AppText size="sm">{row.setByUsername ?? '—'}</AppText>
  },
  {
    key: 'message',
    label: 'Message',
    render: (row) => (
      <AppText size="sm" lineClamp={2} maw={300}>
        {row.message}
      </AppText>
    )
  },
  {
    key: 'isActive',
    label: 'Active',
    render: (row) =>
      row.isActive ? <AppBadge color="green">Yes</AppBadge> : <AppBadge color="gray">No</AppBadge>
  },
  {
    key: 'scheduledExpiresAtUtc',
    label: 'Scheduled end (UTC)',
    render: (row) => formatMotdAuditInstant(row.scheduledExpiresAtUtc)
  },
  {
    key: 'endedAtUtc',
    label: 'Ended (UTC)',
    render: (row) => formatMotdAuditInstant(row.endedAtUtc)
  },
  {
    key: 'endReason',
    label: 'Reason',
    render: (row) => <AppText size="xs">{row.endReason}</AppText>
  },
  {
    key: 'endedByUsername',
    label: 'Ended by',
    render: (row) => <AppText size="sm">{row.endedByUsername ?? '—'}</AppText>
  }
]

function useDebouncedUserSuggestions (query: string) {
  const [suggestions, setSuggestions] = useState<{ value: string }[]>([])
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([])
      return
    }
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const result = await agent.Admin.listUsers({ q: query.trim(), pageSize: 5, pageNumber: 1 })
        setSuggestions((result?.items ?? []).map((u) => ({ value: u.userName })))
      } catch {
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    }, 400)
    return () => clearTimeout(t)
  }, [query])
  return { suggestions, loading }
}

function ReassignModal ({
  opened,
  onClose,
  row,
  onSuccess
}: {
  opened: boolean;
  onClose: () => void;
  row: OrphanedContentDto | null;
  onSuccess: () => void;
}) {
  const { adminStore } = useStore()
  const [username, setUsername] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { suggestions } = useDebouncedUserSuggestions(username)

  const reset = useCallback(() => {
    setUsername('')
    setReason('')
    setError('')
    setSubmitting(false)
  }, [])

  useEffect(() => {
    if (!opened) reset()
  }, [opened, reset])

  const handleSubmit = async () => {
    if (!row) return
    if (reason.trim().length < 10) {
      setError('Reason must be at least 10 characters')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      await adminStore.reassignContent(row.contentType, row.id, username.trim(), reason.trim())
      toast.success('Content reassigned')
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.title || err?.response?.data || err?.message || 'Failed to reassign')
    } finally {
      setSubmitting(false)
    }
  }

  if (!row) return null
  return (
    <AppModal opened={opened} onClose={onClose} title="Reassign content">
      <AppInput
        label="New Owner Username"
        placeholder="Type to search users..."
        value={username}
        onChange={(e) => setUsername(e.currentTarget.value)}
        data={suggestions}
        mb="md"
      />
      <AppTextarea
        label="Reason"
        placeholder="e.g. Original author account deleted during v0.22 migration"
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
        minRows={3}
        error={reason.trim().length > 0 && reason.trim().length < 10 ? 'At least 10 characters' : undefined}
        mb="md"
      />
      {error && (
        <AppText size="sm" color="red" mb="md">
          {error}
        </AppText>
      )}
      <Group>
        <AppButton onClick={handleSubmit} loading={submitting} disabled={submitting || !username.trim() || reason.trim().length < 10}>
          Submit
        </AppButton>
        <AppButton variant="subtle" onClick={onClose} disabled={submitting}>
          Cancel
        </AppButton>
      </Group>
    </AppModal>
  )
}

export default observer(function AdminContentPage () {
  const history = useHistory()
  const { adminStore, userStore } = useStore()
  const [contentType, setContentType] = useState<string | undefined>(undefined)
  const [pageNumberOrphaned, setPageNumberOrphaned] = useState(1)
  const [pageNumberLog, setPageNumberLog] = useState(1)
  const [activeTab, setActiveTab] = useState<AdminContentTab>('0')
  const [reassignModalRow, setReassignModalRow] = useState<OrphanedContentDto | null>(null)
  const [reassignModalOpen, setReassignModalOpen] = useState(false)
  const [bulkUsername, setBulkUsername] = useState('')
  const [bulkReason, setBulkReason] = useState('')
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)
  const [bulkSubmitting, setBulkSubmitting] = useState(false)
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null)
  const [internalTagContentPage, setInternalTagContentPage] = useState(1)
  const [removeInternalTagModal, setRemoveInternalTagModal] = useState<{
    entityTagId: string;
    tagName: string;
  } | null>(null)
  const [applyTagName, setApplyTagName] = useState('')
  const [applyContentType, setApplyContentType] = useState<number>(0)
  const [applyContentId, setApplyContentId] = useState('')
  const [applyError, setApplyError] = useState('')
  const [applySubmitting, setApplySubmitting] = useState(false)
  const [motdCurrent, setMotdCurrent] = useState<MotdDto | null>(null)
  const [motdLoading, setMotdLoading] = useState(false)
  const [motdDraft, setMotdDraft] = useState('')
  const [motdSubmitting, setMotdSubmitting] = useState(false)
  const [motdClearing, setMotdClearing] = useState(false)
  const [motdExpiryUtcDraft, setMotdExpiryUtcDraft] = useState('')
  const [motdHistoryOpen, setMotdHistoryOpen] = useState(false)
  const [motdHistoryPage, setMotdHistoryPage] = useState(1)
  const [motdHistory, setMotdHistory] = useState<PagedResult<MotdHistoryItemDto> | null>(null)
  const [motdHistoryLoading, setMotdHistoryLoading] = useState(false)

  const { suggestions: bulkSuggestions } = useDebouncedUserSuggestions(bulkUsername)

  const loadMotdAdmin = useCallback(async () => {
    setMotdLoading(true)
    try {
      const dto = await agent.Motd.get()
      setMotdCurrent(dto ?? null)
    } catch {
      setMotdCurrent(null)
    } finally {
      setMotdLoading(false)
    }
  }, [])

  useEffect(() => {
    if (userStore.user?.isAdmin) loadMotdAdmin()
  }, [userStore.user?.isAdmin, loadMotdAdmin])

  const loadMotdHistory = useCallback(async () => {
    setMotdHistoryLoading(true)
    try {
      const result = await agent.Motd.history(motdHistoryPage, PAGE_SIZE_MOTD_HISTORY)
      setMotdHistory(result)
    } catch {
      setMotdHistory(null)
      toast.error('Could not load MOTD history.')
    } finally {
      setMotdHistoryLoading(false)
    }
  }, [motdHistoryPage])

  useEffect(() => {
    if (userStore.user?.isAdmin && motdHistoryOpen) void loadMotdHistory()
  }, [userStore.user?.isAdmin, motdHistoryOpen, loadMotdHistory])

  useEffect(() => {
    if (!userStore.user?.isAdmin) {
      history.replace('/admin/users')
    }
  }, [userStore.user?.isAdmin, history])

  useEffect(() => {
    if (!userStore.user?.isAdmin) return
    adminStore.loadOrphanedContent({
      pageNumber: pageNumberOrphaned,
      pageSize: PAGE_SIZE_ORPHANED,
      contentType: contentType || undefined
    })
  }, [userStore.user?.isAdmin, pageNumberOrphaned, contentType])

  useEffect(() => {
    if (activeTab === '2' && userStore.user?.isAdmin) {
      adminStore.loadReassignmentLog({ pageNumber: pageNumberLog, pageSize: PAGE_SIZE_LOG })
    }
  }, [activeTab, pageNumberLog, userStore.user?.isAdmin])

  useEffect(() => {
    if (activeTab === '3' && userStore.user?.isAdmin) {
      adminStore.loadInternalTags()
    }
  }, [activeTab, userStore.user?.isAdmin])

  useEffect(() => {
    if (activeTab === '3' && adminStore.selectedInternalTag && userStore.user?.isAdmin) {
      adminStore.loadInternalTagContent(adminStore.selectedInternalTag, {
        pageNumber: internalTagContentPage,
        pageSize: 25
      })
    }
  }, [activeTab, adminStore.selectedInternalTag, internalTagContentPage, userStore.user?.isAdmin])

  const handleViewInternalTagContent = (tagName: string) => {
    adminStore.loadInternalTagContent(tagName, { pageNumber: 1, pageSize: 25 })
    setInternalTagContentPage(1)
  }

  const handleApplyInternalTag = async () => {
    if (!applyTagName.trim() || !applyContentId.trim()) {
      setApplyError('Tag name and content ID are required.')
      return
    }
    setApplyError('')
    setApplySubmitting(true)
    try {
      await adminStore.applyInternalTag(applyTagName.trim(), applyContentType, applyContentId.trim())
      toast.success('Internal tag applied')
      if (adminStore.selectedInternalTag === applyTagName.trim()) {
        adminStore.loadInternalTagContent(applyTagName.trim(), {
          pageNumber: internalTagContentPage,
          pageSize: 25
        })
      }
    } catch (err: any) {
      setApplyError(
        err?.response?.data?.title || err?.response?.data?.detail || err?.response?.data || err?.message || 'Failed to apply tag'
      )
    } finally {
      setApplySubmitting(false)
    }
  }

  const handleRemoveInternalTagConfirm = async () => {
    if (!removeInternalTagModal) return
    try {
      await adminStore.removeInternalTag(removeInternalTagModal.entityTagId)
      toast.success('Internal tag removed')
      setRemoveInternalTagModal(null)
    } catch {
      // toast from interceptor
    }
  }

  const handleFilterChange = (key: string, value: unknown) => {
    if (key === 'contentType') setContentType((value as string) || undefined)
    setPageNumberOrphaned(1)
  }

  const openReassignModal = (row: OrphanedContentDto) => {
    setReassignModalRow(row)
    setReassignModalOpen(true)
  }

  const selectedCount = adminStore.selectedOrphanedItems.size
  const selectedTypes = useMemo(() => {
    const types = new Set<string>()
    adminStore.selectedOrphanedDetails.forEach((d) => types.add(d.contentType))
    return Array.from(types)
  }, [adminStore.selectedOrphanedDetails])

  const orphanedColumns: ColumnDef<OrphanedContentDto>[] = [
    {
      key: 'id',
      label: 'Type',
      render: (row) => {
        const color =
          row.contentType === 'Post'
            ? 'blue'
            : row.contentType === 'Part'
              ? 'green'
              : row.contentType === 'FirearmModel'
                ? 'navy'
                : 'gray'
        return <AppBadge color={color}>{row.contentType}</AppBadge>
      }
    },
    { key: 'title', label: 'Title', render: (r) => <AppText lineClamp={1}>{r.title || '—'}</AppText> },
    {
      key: 'ownerId',
      label: 'Original Owner ID',
      render: (r) => (
        <AppText component="code" size="xs">
          {r.ownerId?.length > 12 ? `${r.ownerId.slice(0, 12)}...` : r.ownerId || '—'}
        </AppText>
      )
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (r) => (r.createdAt ? format(new Date(r.createdAt), 'MMM d, yyyy') : '—')
    },
    {
      key: 'canBeHidden',
      label: 'Can Be Hidden',
      render: (r) => (r.canBeHidden ? <FiCheck size={16} /> : <FiMinus size={16} />)
    },
    {
      key: 'select',
      label: 'Select',
      render: (r) => (
        <AppCheckbox
          checked={adminStore.selectedOrphanedItems.has(orphanedItemId(r))}
          onChange={() => adminStore.toggleOrphanSelection(orphanedItemId(r), r)}
        />
      )
    },
    {
      key: 'reassign',
      label: 'Actions',
      render: (r) => (
        <AppButton size="xs" variant="light" onClick={() => openReassignModal(r)}>
          Reassign
        </AppButton>
      )
    }
  ]

  const paginationOrphaned = adminStore.orphanedContent
    ? {
        totalCount: adminStore.orphanedContent.totalCount,
        pageNumber: adminStore.orphanedContent.pageNumber,
        pageSize: PAGE_SIZE_ORPHANED
      }
    : { totalCount: 0, pageNumber: 1, pageSize: PAGE_SIZE_ORPHANED }

  const handleBulkReassignConfirm = async () => {
    if (bulkReason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters')
      return
    }
    setBulkConfirmOpen(false)
    setBulkSubmitting(true)
    try {
      await adminStore.bulkReassign(bulkUsername.trim(), bulkReason.trim())
      toast.success('Bulk reassign completed')
      adminStore.loadOrphanedContent({
        pageNumber: pageNumberOrphaned,
        pageSize: PAGE_SIZE_ORPHANED,
        contentType: contentType || undefined
      })
    } catch (err: any) {
      toast.error(err?.response?.data?.title || err?.response?.data || 'Bulk reassign failed')
    } finally {
      setBulkSubmitting(false)
    }
  }

  if (!userStore.user?.isAdmin) return null

  return (
    <Page title="Content Management">
      <Stack spacing="xl">
        {/* MOTD is NOT inside Tabs — always visible, loads on page mount */}
        <AppCard
          withBorder
          padding="lg"
          radius="md"
          sx={{
            borderColor: 'var(--gs-accent)',
            borderWidth: 2,
            backgroundColor: 'var(--gs-surface)',
            boxShadow: '0 0 0 1px rgba(232, 93, 4, 0.15)'
          }}
        >
          <Group position="apart" mb="sm" align="flex-start">
            <Box>
              <AppText
                sx={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 700,
                  fontSize: 18,
                  letterSpacing: 1,
                  color: 'var(--gs-text)'
                }}
              >
                MESSAGE OF THE DAY
              </AppText>
              <AppText size="sm" color="dimmed" mt={4}>
                Shown sitewide under the nav. Resets daily at midnight EST.
              </AppText>
            </Box>
            <AppButton
              variant="light"
              color="gray"
              size="xs"
              onClick={() => void loadMotdAdmin()}
              loading={motdLoading}
            >
              Refresh status
            </AppButton>
          </Group>

          {motdLoading ? (
            <AppText size="sm" color="dimmed" py="md">
              Loading current message…
            </AppText>
          ) : motdCurrent ? (
            <Box
              mb="lg"
              p="md"
              sx={{
                backgroundColor: 'var(--gs-surface2)',
                border: '1px solid var(--gs-border)',
                borderRadius: 8
              }}
            >
              <AppText size="xs" color="dimmed" mb={6}>
                Active since {formatMotdSetAtLabel(motdCurrent.setAt)}
                {motdCurrent.setByUsername ? ` · Set by ${motdCurrent.setByUsername}` : ''}
                {motdCurrent.scheduledExpiresAtUtc
                  ? ` · Auto-expires ${formatMotdAuditInstant(motdCurrent.scheduledExpiresAtUtc)}`
                  : ''}
              </AppText>
              <AppText size="md" sx={{ fontStyle: 'italic', lineHeight: 1.5 }}>
                &ldquo;{motdCurrent.message}&rdquo;
              </AppText>
              <AppButton
                variant="outline"
                color="red"
                size="sm"
                mt="md"
                loading={motdClearing}
                disabled={motdClearing}
                onClick={async () => {
                  setMotdClearing(true)
                  try {
                    await agent.Motd.clear()
                    toast.success('Message of the Day cleared.')
                    await loadMotdAdmin()
                  } catch {
                    /* interceptor */
                  } finally {
                    setMotdClearing(false)
                  }
                }}
              >
                Clear active MOTD
              </AppButton>
            </Box>
          ) : (
            <AppText size="sm" color="dimmed" mb="lg">
              No active message right now.
            </AppText>
          )}

          <AppTextarea
            label="Set or replace the message"
            description="Up to 500 characters. Everyone sees this below the main navigation."
            placeholder="Type today’s community message…"
            minRows={4}
            maxLength={500}
            value={motdDraft}
            onChange={(e) => setMotdDraft(e.currentTarget.value)}
            mb="xs"
            autosize
          />
          <AppText size="xs" color="dimmed" mb="md">
            {motdDraft.length} / 500
          </AppText>
          <AppInput
            label="Optional auto-expiry (UTC)"
            description='ISO 8601 instant, e.g. 2026-04-15T04:00:00Z. Leave blank for no scheduled end.'
            placeholder="2026-04-15T04:00:00Z"
            value={motdExpiryUtcDraft}
            onChange={(e) => setMotdExpiryUtcDraft(e.currentTarget.value)}
            mb="md"
          />
          <AppButton
            type="button"
            size="md"
            color="ember"
            loading={motdSubmitting}
            disabled={motdSubmitting || !motdDraft.trim()}
            onClick={async () => {
              const msg = motdDraft.trim()
              if (!msg) return
              setMotdSubmitting(true)
              try {
                await agent.Motd.set(msg, motdExpiryUtcDraft.trim() || null)
                toast.success('Message of the Day updated.')
                setMotdDraft('')
                setMotdExpiryUtcDraft('')
                await loadMotdAdmin()
              } catch {
                /* interceptor */
              } finally {
                setMotdSubmitting(false)
              }
            }}
          >
            Publish message of the day
          </AppButton>

          <Box mt="xl" pt="md" sx={{ borderTop: '1px solid var(--gs-border)' }}>
            <Group position="apart" mb="sm">
              <AppText weight={600} size="sm">
                MOTD audit log
              </AppText>
              <AppButton size="xs" variant="light" onClick={() => setMotdHistoryOpen((o) => !o)}>
                {motdHistoryOpen ? 'Hide history' : 'Show history'}
              </AppButton>
            </Group>
            <Collapse in={motdHistoryOpen}>
              <Box mt="sm">
                <AdminTable<MotdHistoryItemDto>
                  columns={MOTD_HISTORY_COLUMNS}
                  data={motdHistory?.items ?? []}
                  pagination={{
                    totalCount: motdHistory?.totalCount ?? 0,
                    pageNumber: motdHistoryPage,
                    pageSize: PAGE_SIZE_MOTD_HISTORY
                  }}
                  onPageChange={setMotdHistoryPage}
                  loading={motdHistoryLoading}
                />
              </Box>
            </Collapse>
          </Box>
        </AppCard>

        {/* ── Feature Flags ────────────────────────────────────────── */}
        <AppCard
          withBorder
          p="lg"
          mb="xl"
          sx={{
            borderColor: 'var(--gs-accent)',
            borderWidth: 2
          }}
        >
          <Group align="center" spacing="xs" mb="md">
            <FiSettings size={20} color="var(--gs-accent)" />
            <AppText
              weight={700}
              size="lg"
              sx={{ fontFamily: "'Oswald', sans-serif", letterSpacing: 1 }}
            >
              FEATURE FLAGS
            </AppText>
          </Group>
          <FeatureFlagsAdminPanel />
        </AppCard>

        <Box>
          <AppText weight={600} size="sm" color="dimmed" mb="sm" sx={{ letterSpacing: 0.5 }}>
            Other content tools
          </AppText>
          <AppTabs value={activeTab} onTabChange={(v) => setActiveTab(parseAdminContentTab(v))}>
        <AppTabs.List>
          <AppTabs.Tab value="0">Orphaned Content</AppTabs.Tab>
          <AppTabs.Tab value="1">Bulk Reassign</AppTabs.Tab>
          <AppTabs.Tab value="2">Reassignment Log</AppTabs.Tab>
          <AppTabs.Tab value="3">Internal Tags</AppTabs.Tab>
          <AppTabs.Tab value="4">Pending Cartridge Specs</AppTabs.Tab>
          <AppTabs.Tab value="5">Pending Ammo Products</AppTabs.Tab>
          <AppTabs.Tab value="6">Firearm Platforms</AppTabs.Tab>
        </AppTabs.List>

        <AppTabs.Panel value="0" pt="md">
          <FilterBar
            filters={[
              { key: 'contentType', label: 'Content Type', type: 'select', options: CONTENT_TYPE_OPTIONS }
            ]}
            onFilterChange={handleFilterChange}
          />
          {selectedCount > 0 && (
            <Group position="apart" mb="md" mt="md">
              <AppText size="sm">{selectedCount} items selected</AppText>
              <Group>
                <AppButton variant="subtle" size="sm" onClick={() => adminStore.clearSelection()}>
                  Clear
                </AppButton>
                <AppButton size="sm" onClick={() => setActiveTab(1)}>
                  Bulk Reassign →
                </AppButton>
              </Group>
            </Group>
          )}
          <Box mt="md">
            <AdminTable<OrphanedContentDto>
              columns={orphanedColumns}
              data={adminStore.orphanedContent?.items ?? []}
              pagination={paginationOrphaned}
              onPageChange={setPageNumberOrphaned}
              loading={adminStore.loadingOrphaned}
            />
          </Box>
        </AppTabs.Panel>

        <AppTabs.Panel value="1" pt="md">
          {selectedCount === 0
            ? (
            <EmptyState
              title="No items selected"
              message="Select items from the Orphaned Content tab to bulk reassign them."
            />
              )
            : (
            <>
              <AppText size="sm" mb="sm">
                {selectedCount} items selected across {selectedTypes.length} content types
              </AppText>
              <Box mb="md" sx={{ maxHeight: 200, overflow: 'auto' }}>
                {Array.from(adminStore.selectedOrphanedItems).map((id) => {
                  const detail = adminStore.selectedOrphanedDetails.get(id)
                  const [ct] = id.split('|')
                  return (
                    <Group key={id} position="apart" mb="xs">
                      <Group spacing="xs">
                        <AppBadge size="sm">{detail?.contentType ?? ct}</AppBadge>
                        <AppText size="sm">{detail?.title ?? id}</AppText>
                      </Group>
                      <AppButton
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={() => adminStore.toggleOrphanSelection(id)}
                      >
                        ×
                      </AppButton>
                    </Group>
                  )
                })}
              </Box>
              <AppInput
                label="New Owner Username"
                placeholder="Type to search users..."
                value={bulkUsername}
                onChange={(e) => setBulkUsername(e.currentTarget.value)}
                data={bulkSuggestions}
                mb="md"
              />
              <AppTextarea
                label="Reason"
                placeholder="e.g. Original author account deleted during v0.22 migration"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.currentTarget.value)}
                minRows={3}
                mb="md"
              />
              {adminStore.lastBulkReassignResult && (
                <Box mb="md">
                  <AppText size="sm" color="green" weight={500}>
                    Succeeded: {adminStore.lastBulkReassignResult.succeeded}
                  </AppText>
                  {adminStore.lastBulkReassignResult.failures.length > 0 && (
                    <Box mt="xs">
                      {adminStore.lastBulkReassignResult.failures.map((f, i) => (
                        <AppText key={i} size="sm" color="red">
                          {f.contentType} {f.contentId}: {f.reason}
                        </AppText>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              <AppButton
                onClick={() => setBulkConfirmOpen(true)}
                disabled={!bulkUsername.trim() || bulkReason.trim().length < 10 || bulkSubmitting}
                loading={bulkSubmitting}
              >
                Reassign {selectedCount} items
              </AppButton>
            </>
              )}
          <ConfirmModal
            opened={bulkConfirmOpen}
            onClose={() => setBulkConfirmOpen(false)}
            title="Confirm bulk reassign"
            message={`You are about to reassign ${selectedCount} content items to ${bulkUsername || '?'}. This action is logged and cannot be undone automatically.`}
            confirmLabel="Reassign"
            onConfirm={handleBulkReassignConfirm}
            variant="warning"
          />
        </AppTabs.Panel>

        <AppTabs.Panel value="3" pt="md">
          <SidebarLayout sidebarWidth={380} sidebar={
            adminStore.loadingInternalTags
              ? (
              <AppText color="dimmed">Loading tags…</AppText>
                )
              : (
              <Box>
                <AppText weight={600} mb="sm">Tag usage</AppText>
                <Stack spacing="sm">
                  {(adminStore.internalTags ?? []).map((tag: InternalTagSummaryDto) => (
                    <AppCard key={tag.id} withBorder padding="md" radius="md" sx={{ opacity: tag.totalUsage === 0 ? 0.85 : 1 }}>
                      <Group position="apart" mb="xs">
                        <AppText weight={600} size="sm">{tag.displayName || tag.name}</AppText>
                        {tag.totalUsage === 0 && <AppBadge size="sm" color="gray">Unused</AppBadge>}
                      </Group>
                      <AppText size="xl" weight={700} mb="xs">{tag.totalUsage}</AppText>
                      <Group spacing="sm">
                        <AppText size="xs" color="dimmed">Posts: {tag.postUsage}</AppText>
                        <AppText size="xs" color="dimmed">Parts: {tag.partUsage}</AppText>
                        <AppText size="xs" color="dimmed">Models: {tag.modelUsage}</AppText>
                        <AppText size="xs" color="dimmed">Builds: {tag.buildUsage}</AppText>
                      </Group>
                      <AppButton size="xs" variant="light" mt="sm" fullWidth onClick={() => handleViewInternalTagContent(tag.name)}>
                        View Content →
                      </AppButton>
                    </AppCard>
                  ))}
                </Stack>
              </Box>
                )
          } main={
            <Box>
              <Box mb="md" sx={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8, padding: 16 }}>
                <AppText size="sm" weight={600} mb="xs">Apply internal tag</AppText>
                <Group align="flex-end" spacing="sm">
                  <AppSelect
                    placeholder="Tag"
                    data={(adminStore.internalTags ?? []).map((t: InternalTagSummaryDto) => ({ value: t.name, label: t.displayName || t.name }))}
                    value={applyTagName}
                    onChange={(v) => setApplyTagName(v ?? '')}
                    sx={{ minWidth: 160 }}
                  />
                  <AppSelect
                    placeholder="Content type"
                    data={[
                      { value: '0', label: 'Post' },
                      { value: '1', label: 'Part' },
                      { value: '2', label: 'FirearmModel' },
                      { value: '3', label: 'Build' }
                    ]}
                    value={String(applyContentType)}
                    onChange={(v) => setApplyContentType(v != null ? parseInt(v, 10) : 0)}
                    sx={{ minWidth: 140 }}
                  />
                  <AppInput
                    placeholder="Content ID (GUID)"
                    value={applyContentId}
                    onChange={(e) => setApplyContentId(e.currentTarget.value)}
                    sx={{ minWidth: 280 }}
                  />
                  <AppButton onClick={handleApplyInternalTag} loading={applySubmitting} disabled={applySubmitting}>
                    Apply Tag
                  </AppButton>
                </Group>
                {applyError && <AppText size="sm" color="red" mt="xs">{applyError}</AppText>}
              </Box>
              {!adminStore.selectedInternalTag
                ? (
                <EmptyState title="Select a tag" message="Choose an internal tag from the left to view its content." />
                  )
                : (
                <>
                  <AppText weight={600} mb="sm">
                    {adminStore.internalTags?.find((t: InternalTagSummaryDto) => t.name === adminStore.selectedInternalTag)?.displayName || adminStore.selectedInternalTag}
                  </AppText>
                  <AdminTable<InternalTagContentDto>
                    columns={[
                      { key: 'contentType', label: 'Type', render: (r) => <AppBadge size="sm">{r.contentType}</AppBadge> },
                      {
                        key: 'contentTitle',
                        label: 'Title',
                        render: (r) => {
                          const href =
                            r.contentType === 'Post'
                              ? `/blog/${r.contentId}`
                              : r.contentType === 'Part'
                                ? '/parts-bin'
                                : r.contentType === 'FirearmModel'
                                  ? `/registry/${r.contentId}`
                                  : r.contentType === 'Build' ? `/builds/${r.contentId}` : '#'
                          return <AppText component={Link} to={href} size="sm">{r.contentTitle || '—'}</AppText>
                        }
                      },
                      { key: 'ownerUsername', label: 'Owner', render: (r) => <AppText size="sm">{r.ownerUsername}</AppText> },
                      { key: 'appliedByUsername', label: 'Applied by', render: (r) => <AppText size="sm">{r.appliedByUsername}</AppText> },
                      { key: 'appliedAt', label: 'Applied at', render: (r) => format(new Date(r.appliedAt), 'MMM d, yyyy HH:mm') },
                      {
                        key: 'actions',
                        label: 'Actions',
                        render: (r) => (
                          <AppButton
                            size="xs"
                            color="red"
                            variant="light"
                            onClick={() => setRemoveInternalTagModal({ entityTagId: r.entityTagId, tagName: adminStore.selectedInternalTag! })}
                          >
                            Remove Tag
                          </AppButton>
                        )
                      }
                    ]}
                    data={adminStore.internalTagContent?.items ?? []}
                    pagination={{
                      totalCount: adminStore.internalTagContent?.totalCount ?? 0,
                      pageNumber: internalTagContentPage,
                      pageSize: 25
                    }}
                    onPageChange={setInternalTagContentPage}
                    loading={false}
                  />
                </>
                  )}
            </Box>
          } />
          <ConfirmModal
            opened={!!removeInternalTagModal}
            onClose={() => setRemoveInternalTagModal(null)}
            title="Remove internal tag"
            message={removeInternalTagModal ? `Remove internal tag '${removeInternalTagModal.tagName}' from this content? This action is logged.` : ''}
            confirmLabel="Remove"
            onConfirm={handleRemoveInternalTagConfirm}
            variant="danger"
          />
        </AppTabs.Panel>

        <AppTabs.Panel value="2" pt="md">
          {!adminStore.reassignmentLog?.items?.length && !adminStore.loadingReassignmentLog
            ? (
            <EmptyState title="No log entries" message="Reassignment log will appear here." />
              )
            : (
            <Box>
              <Table striped highlightOnHover>
                <thead>
                  <tr>
                    <th>Admin</th>
                    <th>Action</th>
                    <th>Detail</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(adminStore.reassignmentLog?.items ?? []).map((entry) => (
                    <>
                      <tr
                        key={entry.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setExpandedLogId(expandedLogId === entry.id ? null : entry.id)}
                      >
                        <td>{entry.adminUsername}</td>
                        <td>
                          <AppBadge size="sm">{entry.action}</AppBadge>
                        </td>
                        <td>
                          <AppText size="sm">
                            {entry.detail?.length > 80 ? `${entry.detail.slice(0, 80)}...` : entry.detail}
                          </AppText>
                        </td>
                        <td>{entry.createdAt ? format(new Date(entry.createdAt), 'MMM d, yyyy HH:mm') : '—'}</td>
                      </tr>
                      <tr key={`${entry.id}-exp`}>
                        <td colSpan={4} style={{ padding: 0, border: 0, verticalAlign: 'top' }}>
                          <Collapse in={expandedLogId === entry.id}>
                            <Box
                              sx={{
                                padding: 12,
                                backgroundColor: 'var(--mantine-color-gray-0)',
                                borderBottom: '1px solid var(--mantine-color-gray-2)'
                              }}
                            >
                              <AppText size="sm" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {entry.detail}
                              </AppText>
                            </Box>
                          </Collapse>
                        </td>
                      </tr>
                    </>
                  ))}
                </tbody>
              </Table>
              {adminStore.reassignmentLog && adminStore.reassignmentLog.totalCount > PAGE_SIZE_LOG && (
                <Pagination
                  total={Math.ceil(adminStore.reassignmentLog.totalCount / PAGE_SIZE_LOG)}
                  page={pageNumberLog}
                  onChange={setPageNumberLog}
                  position="right"
                  mt="md"
                />
              )}
            </Box>
              )}
        </AppTabs.Panel>

        <AppTabs.Panel value="4" pt="md">
          <EmptyState title="Pending Cartridge Specs" message="Cartridge spec management coming soon." />
        </AppTabs.Panel>

        <AppTabs.Panel value="5" pt="md">
          <EmptyState title="Pending Ammo Products" message="Ammo product management coming soon." />
        </AppTabs.Panel>

        <AppTabs.Panel value="6" pt="md">
          <PlatformsAdminPanel />
        </AppTabs.Panel>
      </AppTabs>
        </Box>
      </Stack>

      <ReassignModal
        opened={reassignModalOpen}
        onClose={() => { setReassignModalOpen(false); setReassignModalRow(null) }}
        row={reassignModalRow}
        onSuccess={() => {}}
      />
    </Page>
  )
})
